import { AgTechAppLoadingStep } from "@agtech/core/app/AgTechApplication"
import { AgTechDataAction, useDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions"
import { AgTechApiEntityResponse, convertFromEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests"
import { useAgTechGetRequest } from "@agtech/core/data/http/AgTechApiGetRequests"
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests"
import { SaleLot } from "../lots/LotData"
import { SaleLotCategory } from "../lots/categories/LotCategoryData"
import { Sale } from "./SaleData"
import { SaleDiscount } from "./discounts/SaleDiscountData"
import { salesDataSlice } from "./SaleDataStore"
import { useAgTechQuery, waitOnQuery } from "@agtech/core/data/queries/AgTechDataQueries"
import { SaleDataGetSalesQuery, SalePurchaseDataDTO } from "./SaleDataQueries"

/// Sale Data DTOs

export type SaleCreateDTO = {
    sale: Sale,
    productId?: number
}

type SaleInformationDTO = {
    saleId: number,
    saleName: string,
    saleStartDate: Date,
    saleEndDate: Date,
}

type SaleBidderDTO = {
    bidderId: number,
    bidderNumber: string,
    contactId: number,
    date: Date,
    saleId: number
}

type SaleLotDTO = {
    lotId: number,
    lotNumber: string,
    saleOrder: number,
    lotDescription: string,
    bidderId: number,
    bidderNumber: string,
    purchasePrice: number,
    timestamp: string,
    invoiceState: number,
    isSplit: boolean
}

export type SaleSelectionDTO = {
    id: number;
    saleName: string;
    saleStartDate: Date;
    saleEndDate: Date;
}

type SaleDetailDTO = {
    sale: SaleInformationDTO,
    sales: SaleSelectionDTO[],
    bidders: SaleBidderDTO[],
    lots: SaleLotDTO[],
    lotCategories: SaleLotCategory[],
    discounts: SaleDiscount[]
}

export type SaleCreationDTO = {
    sale: Sale,
    productId: number,
    onSaleCreated: (result: SaleCreationResult) => Promise<boolean>
}

// Sale Data Actions

export const useSaleDataChangeSelectedSaleAction = (): AgTechDataAction<number | undefined, SaleDetailDTO> => {
    let saleDataGetRequest = useAgTechGetRequest<SaleDetailDTO>();

    return {
        name: 'ChangeSelectedSale',
        action: async props => {
            return await saleDataGetRequest.get({
                baseURL: process.env.REACT_APP_API_URL + '', // Specify the base URL manually as this may execute outside of app context
                path: props.submittedEntity ? 'Sales/Data/' + props.submittedEntity : 'Sales/Data'
            });
        },
        onSuccess: async props => {
            props.executeReducerAction(salesDataSlice.actions.setCurrentSale, {
                id: props.responseData.sale.saleId,
                saleName: props.responseData.sale.saleName,
                saleStartDate: props.responseData.sale.saleStartDate,
                saleEndDate: props.responseData.sale.saleEndDate
            });

            props.queryClient.clear();
        }
    }
}

export type SaleCreationData = {
    sale: Sale,
    lots: SaleLot[]
}

export type SaleCreationResult = {
    sale: Sale,
    purchaseIntent: SalePurchaseDataDTO
}

export const useSaleDataWriteSaleAction = (): AgTechDataAction<SaleCreationDTO, SaleCreationResult> => {
    const existingSalesQuery = useAgTechQuery({
        query: SaleDataGetSalesQuery
    });

    const changeSaleAction = useSaleDataChangeSelectedSaleAction();

    return {
        name: 'WriteSale',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.sale?.id ? 'Updating sale...' : 'Creating sale...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.sale?.id ? 'updated' : 'created'} sale`
        }),
        validate: async props => {
            const existingSales = await waitOnQuery(existingSalesQuery);

            props.validation.failWithWarningIf(
                existingSales.filter(sl => sl.saleName === props.submittedEntity.sale.saleName).length > 0,
                'A sale with this name already exists. Please select a new name for this sale.'
            );
        },
        action: async props => {
            return await props.http.post<any, AgTechApiEntityResponse<SaleCreationResult>, SaleCreationResult>({
                request: 'Sales/SubmitForCheckout',
                inputData: {
                    ...props.submittedEntity.sale,
                    productId: props.submittedEntity.productId
                },
                convert: response => response.data
            });
        },
        onSuccess: async props => {
            const creationHandle = await props.submittedEntity.onSaleCreated(props.responseData);

            if (creationHandle) {
                await props.actionExecutor.executeAction(changeSaleAction, {
                    submittedEntity: props.responseData.sale.id
                });
            }
        }
    }
}